import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { getLevel as getStaticLevel } from '../data/kariyerCurriculum';
import { fetchLevel } from '../services/curriculumService';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, CheckCircle2, Star, Play,
  BookOpen, Mic, HelpCircle, Award, Video,
  GripHorizontal, Puzzle, Headphones, MessageSquare, Timer, Layers, Volume2
} from 'lucide-react';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { KariyerLogoIcon } from '../components/KariyerLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LESSON_TYPE_ICONS = {
  flashcard: { icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-100' },
  quiz: { icon: HelpCircle, color: 'text-purple-500', bg: 'bg-purple-100' },
  audio: { icon: Mic, color: 'text-green-500', bg: 'bg-green-100' },
  scenario: { icon: Play, color: 'text-amber-500', bg: 'bg-amber-100' },
  exam: { icon: Award, color: 'text-red-500', bg: 'bg-red-100' },
  video: { icon: Video, color: 'text-pink-500', bg: 'bg-pink-100' },
  dragdrop: { icon: GripHorizontal, color: 'text-cyan-500', bg: 'bg-cyan-100' },
  fillin: { icon: Puzzle, color: 'text-orange-500', bg: 'bg-orange-100' },
  matching: { icon: Puzzle, color: 'text-teal-500', bg: 'bg-teal-100' },
  vocabulary: { icon: BookOpen, color: 'text-emerald-500', bg: 'bg-emerald-100' },
  speaking: { icon: Mic, color: 'text-rose-500', bg: 'bg-rose-100' },
};

export default function KariyerLevel() {
  const { levelId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState(null);

  useEffect(() => {
    loadLevel();
    fetchProgress();
  }, [levelId]);

  const loadLevel = async () => {
    try {
      // Önce API'den dene
      const apiLevel = await fetchLevel(levelId, 'english');
      if (apiLevel) {
        setLevel(apiLevel);
        return;
      }
    } catch (error) {
      console.log('API error, falling back to static');
    }
    // Fallback: Static dosyadan yükle
    setLevel(getStaticLevel(levelId));
  };

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/kariyer/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!level) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-800 text-center">
          <p>Seviye bulunamadı</p>
          <Button onClick={() => navigate('/english-training')} className="mt-4">
            Eğitime Dön
          </Button>
        </div>
      </div>
    );
  }

  const completedLessons = progress?.unit_progress || {};

  const isLessonCompleted = (unitId, lessonId) => {
    return completedLessons[`${unitId}`]?.includes(lessonId) || false;
  };

  const isUnitLocked = (unitIndex) => {
    // Tüm üniteler açık - kilit yok
    return false;
  };

  // Tüm dersler açık
  const isLessonLocked = (unitId, lessonIndex) => {
    return false;
  };

  const getUnitProgress = (unit) => {
    const completed = (completedLessons[`${unit.id}`] || []).length;
    return (completed / unit.lessons.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mavi Gradient */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate('/english-training')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-4xl shadow-lg">
              {level.icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-white">
                  {level.level_code === 'INT' ? level.title : `Seviye ${level.level_code}: ${level.title}`}
                </h1>
                {level.level_code !== 'INT' && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-white font-medium">
                    {level.level_code}
                  </span>
                )}
              </div>
              <p className="text-blue-100 text-sm">{level.titleTr || level.description}</p>
              <p className="text-blue-200 text-xs mt-1">
                {level.units.length} ünite • ~{level.estimatedDays} gün
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Üniteler */}
      <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
        <div className="space-y-6">
          {level.units.map((unit, unitIndex) => {
            const unitProgress = getUnitProgress(unit);
            
            return (
              <div key={unit.id} className="relative">
                {/* Ünite Başlığı */}
                <Card className="border-0 shadow-md mb-4 bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md bg-gradient-to-br from-blue-400 to-blue-600">
                        <span className="text-white">{unit.icon}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-800">
                          Ünite {unit.id}: {unit.title}
                        </h3>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{unit.lessons.length} ders</span>
                            <span>{Math.round(unitProgress)}%</span>
                          </div>
                          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transition-all"
                              style={{ width: `${unitProgress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dersler - Tüm dersler açık */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {unit.lessons.map((lesson, lessonIndex) => {
                    const completed = isLessonCompleted(unit.id, lesson.id);
                    const typeConfig = LESSON_TYPE_ICONS[lesson.type] || LESSON_TYPE_ICONS.flashcard;
                    const TypeIcon = typeConfig.icon;
                    
                    return (
                      <Card
                        key={lesson.id}
                        className={cn(
                          'border-0 shadow-md transition-all cursor-pointer',
                          completed ? 'bg-green-50 hover:shadow-lg' : 
                          'bg-white hover:shadow-lg hover:scale-105'
                        )}
                        onClick={() => navigate(`/lesson/${levelId}/${unit.id}/${lesson.id}`)}
                      >
                        <CardContent className="p-4 relative">
                          {/* Tamamlanma İşareti */}
                          {completed && (
                            <div className="absolute -top-2 -right-2">
                              <CheckCircle2 className="w-6 h-6 text-green-500 fill-green-500 bg-white rounded-full" />
                            </div>
                          )}
                          
                          {/* Icon */}
                          <div className={cn(
                            'w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3',
                            typeConfig.bg
                          )}>
                            <TypeIcon className={cn('w-6 h-6', typeConfig.color)} />
                          </div>
                          
                          {/* Başlık */}
                          <p className={cn(
                            'text-sm font-medium text-center line-clamp-2 mb-2',
                            completed ? 'text-green-700' : 'text-gray-700'
                          )}>
                            {lesson.title}
                          </p>
                            
                          {/* Süre & Mil */}
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-xs text-gray-400">{lesson.duration}dk</span>
                            <span className="text-xs text-yellow-500 font-medium">+{lesson.xp} Mil</span>
                          </div>
                        </CardContent>
                        </Card>
                      );
                    })}
                  </div>
              </div>
            );
          })}

          {/* Beceri Testleri - Final Sınavının Yanında */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-rose-50 to-emerald-50">
            <CardContent className="p-5">
              <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                🎯 Beceri Testleri
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Bu seviyeye özel Speaking ve Listening pratikleri
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => navigate(`/skill-test/${levelId}/speaking`)}
                  className="flex items-center justify-center gap-2 py-4 px-4 rounded-xl bg-rose-100 hover:bg-rose-200 border-2 border-rose-300 text-rose-700 font-semibold transition-all"
                >
                  <Mic className="w-5 h-5" />
                  Speaking
                </button>
                <button
                  onClick={() => navigate(`/skill-test/${levelId}/listening`)}
                  className="flex items-center justify-center gap-2 py-4 px-4 rounded-xl bg-emerald-100 hover:bg-emerald-200 border-2 border-emerald-300 text-emerald-700 font-semibold transition-all"
                >
                  <Headphones className="w-5 h-5" />
                  Listening
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
